import { z, type ZodEnum, type ZodSchema } from 'zod';

type Routes = [string, ...string[]];
type Route<T extends Routes> = T[number];
type SerializedNavigationSchema<T extends Routes> = {
  pathname: T[number];
  timestamp: Date;
};

type RoutesSchema<T extends Routes> = ZodSchema<SerializedNavigationSchema<T>>;

export function createSerializedNativationSchema<T extends Routes>(
  routesSchema: ZodEnum<T>
) {
  return z.object({
    pathname: routesSchema,
    timestamp: z.coerce.date(),
  }) as RoutesSchema<T>;
}

export function deserializeNavState<R extends Routes>(
  rawValue: unknown,
  schema: RoutesSchema<R>
) {
  const validate = schema.safeParse(rawValue);
  const { success } = validate;
  if (success === true) {
    return validate.data;
  }
}

const NAV_RESUME_TTL_HOURS = 24;

/** Whether a saved screen is too old to resume (older than 24h). */
export function isNavStateExpired<T extends Routes>(
  navigationState: SerializedNavigationSchema<T>
): boolean {
  const timestamp = new Date(navigationState.timestamp);
  const now = new Date();
  const msDiff = Math.abs(timestamp.getTime() - now.getTime());
  const hrsDiff = msDiff / (60 * 60 * 1000);

  return hrsDiff > NAV_RESUME_TTL_HOURS;
}

export function getNavResumeRoute<T extends Routes>({
  defaultRoute,
  navigationState,
}: {
  navigationState: SerializedNavigationSchema<T>;
  defaultRoute: Route<T>;
}): Route<T> | undefined {
  if (isNavStateExpired(navigationState)) {
    return defaultRoute;
  }

  return navigationState.pathname;
}
